import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { useProduct } from "@/context/ProductContext";
import { ClockCounterClockwise } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cache = {};
const BAND_STYLES = {
  "Launch Ready": { color: "#047857", bg: "#04785712" },
  Advanced: { color: "#1D4ED8", bg: "#1D4ED812" },
  Developing: { color: "#B45309", bg: "#B4530912" },
  Foundational: { color: "#B91C1C", bg: "#B91C1C12" },
};

export const GmariBadge = ({ full = false }) => {
  const navigate = useNavigate();
  const { productId } = useProduct() || {};
  const [g, setG] = useState(() => cache[productId]?.data || null);
  const [meta, setMeta] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    if (!productId) return;
    setHistoryOpen(false);
    const c = cache[productId];
    if (c && Date.now() - c.at < 60000) { setG(c.data); setMeta(c.meta); return; }
    axios.get(`${API}/readiness/${productId}`).then((r) => {
      cache[productId] = { ...(cache[productId] || {}), data: r.data.gmari, at: Date.now() };
      setG(r.data.gmari);
    }).catch(() => {});
    axios.get(`${API}/score-history/${productId}`).then((r) => {
      cache[productId] = { ...(cache[productId] || {}), meta: r.data, at: cache[productId]?.at || Date.now() };
      setMeta(r.data);
    }).catch(() => {});
  }, [productId]);

  if (!g) return null;
  const s = BAND_STYLES[g.band] || BAND_STYLES.Developing;
  const asOf = meta?.as_of ? new Date(meta.as_of).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null;
  return (
    <div className={`relative ${full ? "w-full" : "shrink-0"}`}>
      <div className={`flex items-center gap-1 ${full ? "w-full" : ""}`}>
        <button data-testid={full ? "gmari-badge-sidebar" : "gmari-badge"} onClick={() => navigate("/console")}
          title="GMARI™ — Global Market Access Readiness Index. Click for the full breakdown."
          className={`flex items-center gap-2 border px-3 py-1.5 hover:opacity-80 transition-opacity ${full ? "flex-1 justify-between py-2" : ""}`}
          style={{ borderColor: `${s.color}44`, background: s.bg }}>
          <span className="overline-label" style={{ color: s.color }}>GMARI™</span>
          <span className="font-heading font-extrabold text-base leading-none" style={{ color: s.color }}>{g.score}<span className="text-[10px] font-semibold opacity-60">/100</span></span>
          <span className={`text-[10px] font-semibold uppercase tracking-wider ${full ? "" : "hidden md:inline"}`} style={{ color: s.color }}>{g.band}</span>
        </button>
        <button data-testid={full ? "score-history-btn-sidebar" : "score-history-btn"} onClick={() => setHistoryOpen(!historyOpen)}
          title="Score history — past GMARI changes with reasons"
          className="shrink-0 border px-1.5 py-2 hover:opacity-80" style={{ borderColor: `${s.color}44`, background: s.bg, color: s.color }}>
          <ClockCounterClockwise size={14} />
        </button>
      </div>
      {asOf && full && <p data-testid="gmari-as-of" className="font-mono-data text-[10px] text-slate-400 mt-1 px-0.5">as of {asOf}</p>}
      {historyOpen && (
        <div data-testid="score-history-panel" className="absolute z-50 top-full mt-1.5 left-0 w-80 bg-white border border-slate-300 shadow-lg">
          <div className="px-3.5 py-2.5 border-b border-slate-200 flex items-center justify-between">
            <p className="text-[11px] font-bold text-slate-800">GMARI™ Score History</p>
            {asOf && <span className="font-mono-data text-[10px] text-slate-400">data as of {asOf}</span>}
          </div>
          <div className="max-h-64 overflow-y-auto">
            {(meta?.history || []).length === 0 ? (
              <p className="px-3.5 py-4 text-[11px] text-slate-400">No score changes recorded yet.</p>
            ) : meta.history.map((h, i) => (
              <div key={i} className="px-3.5 py-2.5 border-b border-slate-100 last:border-0">
                <p className="text-[11px] text-slate-700">
                  <span className="font-heading font-extrabold">{h.score}</span>
                  {h.prev_score !== null && <span className="text-slate-400"> (was {h.prev_score})</span>}
                  <span className="text-slate-500"> — {h.reason}</span>
                </p>
                <p className="font-mono-data text-[10px] text-slate-400 mt-0.5">{new Date(h.date).toLocaleString()}</p>
              </div>
            ))}
          </div>
          <Link to="/console/methodology" onClick={() => setHistoryOpen(false)}
            className="block px-3.5 py-2 text-[10px] font-semibold text-slate-500 hover:text-slate-900 border-t border-slate-100">
            How is GMARI™ calculated? → Methodology Note
          </Link>
        </div>
      )}
    </div>
  );
};
